import { PageController } from '@client/components/_core/layout/PageController';
import { VDatePicker } from '@client/components/_shared/forms/view-form/VDatePicker';
import { VDropdown } from '@client/components/_shared/forms/view-form/VDropdown';
import { VEntityOwnerSelect } from '@client/components/_shared/forms/view-form/VEntityOwnerSelect';
import { VLinksArrayInput } from '@client/components/_shared/forms/view-form/VLinksArrayInput';
import { VNumericInput } from '@client/components/_shared/forms/view-form/VNumericInput';
import { VTextInput } from '@client/components/_shared/forms/view-form/VTextInput';
import { VTipTapInput } from '@client/components/_ui/data-entry/TipTap';
import { CardTitle } from '@client/components/_ui/display/CardTitle';
import { VBreachExamples } from '@client/components/obligations/view-form/VBreachExamples';
import { LinkedRegisterRecordsPanel } from '@client/components/registers/LinkedRegisterRecordsPanel';
import { TaskList } from '@client/components/tasks/TaskList';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useConfigService } from '@client/hooks/business/configuration/useConfigService';
import { useObligationFieldAccess } from '@client/hooks/business/obligation/useObligationFieldAccess';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { ICONS } from '@client/styles/theme';
import { requiredFieldRule } from '@client/utils/ui/form';
import { buildNumericInputRules } from '@client/utils/validation/numeric';
import { OBLIGATION_STATUSES, USER_SETTABLE_OBLIGATION_STATUS_KEYS } from '@shared/constants';
import type { ObligationStatus } from '@shared/types';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Col, Row } from 'antd';
import { useCanEditObligation } from './hooks';
import { Controls } from './ObligationDetailsManage/Controls';
import { obligationFormStore } from './store';

export const Route = createFileRoute('/console/$orgId/compliance/obligations/details/$id/manage')({
    staticData: {
        ui: {
            useParentTitle: true,
        },
    },
    component: ObligationDetailsManage,
});

function ObligationDetailsManage() {
    const { trpcClient } = useTrpc();
    const obligation = obligationFormStore.use.formData();
    const canEdit = useCanEditObligation();
    const { isFieldReadonly } = useObligationFieldAccess(obligation);

    const configService = useConfigService();

    const readonly = !canEdit;

    // CUR-644 REC-L2: reverse Linked-records panel — needs register:read on
    // top of obligation access (dual-permission; the server re-checks both).
    // Links pin this obligation VERSION row, so the panel is per-version too.
    // No compliance-module check here (unlike the control detail route, which
    // sits outside it): every route under ~compliance already inherits
    // `modules: ['compliance']` from ~compliance/~route.tsx.
    const registersEnabled = useFeatureFlag('enable-registers', false);
    const { can } = usePermissions();
    const showLinkedRegisterRecords = registersEnabled && can('register:read');

    const VEntityOwnerSelectInstance = VEntityOwnerSelect({
        entity: 'obligation',
        formStore: obligationFormStore,
        isReadOnly: readonly || isFieldReadonly('ownerId') || isFieldReadonly('handlingPartyIds'),
    });

    return (
        <PageController route={Route}>
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={16}>
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Card title={<CardTitle icon={<ICONS.obligation />} title="Obligation" />} size="small">
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={24} md={6}>
                                        <VNumericInput
                                            field="order"
                                            label="ID"
                                            formStore={obligationFormStore}
                                            valueType="number"
                                            precision={0}
                                            rules={buildNumericInputRules({
                                                valueType: 'number',
                                                isRequired: true,
                                                min: 1,
                                            })}
                                            isReadOnly={readonly || isFieldReadonly('order')}
                                        />
                                    </Col>
                                    <Col xs={24} sm={24} md={6}>
                                        <VTextInput
                                            prefix={obligation?.Legislation?.idPrefix || ''}
                                            field="identificationCode"
                                            label="Identification code"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('identificationCode')}
                                            formatValueFn={(_, item) => item.identificationCodeWithPrefix ?? ''}
                                            rules={requiredFieldRule}
                                        />
                                    </Col>
                                    <Col xs={24} sm={24} md={6}>
                                        <VDropdown
                                            label="Status"
                                            field="status"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('status')}
                                            formatValueFn={(value) => OBLIGATION_STATUSES[value as ObligationStatus]}
                                            options={USER_SETTABLE_OBLIGATION_STATUS_KEYS.map((key) => ({
                                                label: OBLIGATION_STATUSES[key],
                                                value: key,
                                            }))}
                                        />
                                    </Col>
                                    <Col xs={24} sm={24} md={6}>
                                        <VEntityOwnerSelectInstance.Owner helpMessage={obligation.ownerHint || ''} />
                                    </Col>
                                    <Col xs={24} sm={24} md={6}>
                                        <VDropdown
                                            formStore={obligationFormStore}
                                            field="country"
                                            label="Country"
                                            isReadOnly={readonly || isFieldReadonly('country')}
                                            loaderFn={async () => {
                                                const data = await configService.fetchCountryList();
                                                return data.map((item) => ({ value: item }));
                                            }}
                                        />
                                    </Col>
                                    <Col xs={24} sm={24} md={6}>
                                        <VDatePicker
                                            field="effectiveDateFrom"
                                            label="Effective From"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('effectiveDateFrom')}
                                            maxDate={
                                                obligation.effectiveDateTo ? obligation.effectiveDateTo : undefined
                                            }
                                            rules={[
                                                {
                                                    async validator(_, value: Date) {
                                                        if (!value) {
                                                            return Promise.resolve();
                                                        }

                                                        if (
                                                            obligation.effectiveDateTo &&
                                                            obligation.effectiveDateTo <= value
                                                        ) {
                                                            throw new Error(
                                                                'Effective From date cannot be later than Effective To date',
                                                            );
                                                        }
                                                    },
                                                },
                                            ]}
                                        />
                                    </Col>
                                    <Col xs={24} sm={24} md={6}>
                                        <VDatePicker
                                            field="effectiveDateTo"
                                            label="Effective To"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('effectiveDateTo')}
                                            minDate={
                                                obligation.effectiveDateFrom ? obligation.effectiveDateFrom : undefined
                                            }
                                            rules={[
                                                {
                                                    async validator(_, value: Date) {
                                                        if (!value) {
                                                            return Promise.resolve();
                                                        }

                                                        if (
                                                            obligation.effectiveDateFrom &&
                                                            value <= obligation.effectiveDateFrom
                                                        ) {
                                                            throw new Error(
                                                                'Effective To date cannot be earlier than Effective From date',
                                                            );
                                                        }
                                                    },
                                                },
                                            ]}
                                        />
                                    </Col>
                                    <Col xs={24} sm={24} md={18}>
                                        <VEntityOwnerSelectInstance.HandlingParty />
                                    </Col>
                                    <Col xs={24} sm={24} md={12}>
                                        <VDropdown
                                            field="legislationId"
                                            label="Legislation"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('legislationId')}
                                            loaderFn={async () => {
                                                const { rows } =
                                                    await trpcClient.legislation.listLegislationItems.query({
                                                        limit: 100,
                                                        orderBy: { name: 'asc' },
                                                    });

                                                return rows.map(({ id, name }) => ({
                                                    label: name,
                                                    value: id,
                                                }));
                                            }}
                                            formatValueFn={(_, { Legislation }) => Legislation?.name}
                                        />
                                    </Col>

                                    <Col span={24}>
                                        <VTextInput
                                            field="coreObligation"
                                            label="Core obligation"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('coreObligation')}
                                        />
                                    </Col>
                                    <Col span={24}>
                                        <VTipTapInput
                                            field="whatItMeans"
                                            label="What it means"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('whatItMeans')}
                                            maxRowsToDisplay={5}
                                        />
                                    </Col>
                                    <Col span={24}>
                                        <VTipTapInput
                                            field="howWeAreComplying"
                                            label="How we are complying"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('howWeAreComplying')}
                                            maxRowsToDisplay={5}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                        <Col span={24}>
                            <Card
                                title={<CardTitle icon={<ICONS.obligationSource />} title="Source of obligation" />}
                                size="small"
                            >
                                <Row gutter={[16, 16]}>
                                    <Col span={12}>
                                        <VTextInput
                                            field="section"
                                            label="Sub obligation / section / paragraph"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('section')}
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <VTextInput
                                            field="source"
                                            label="Source"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('source')}
                                        />
                                    </Col>
                                    <Col span={24}>
                                        <VTipTapInput
                                            field="legislationDescription"
                                            label="Exact wording from the legislation"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('legislationDescription')}
                                            maxRowsToDisplay={5}
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <VLinksArrayInput
                                            label="Austlii link"
                                            field="austLIIURLs"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('austLIIURLs')}
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <VLinksArrayInput
                                            label="Federal Register of Legislation link"
                                            field="federalRegisterOfLegislationURLs"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('federalRegisterOfLegislationURLs')}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                        <Col span={24}>
                            <Card
                                title={<CardTitle icon={<ICONS.regulatoryGuidance />} title="Regulatory guidance" />}
                                size="small"
                            >
                                <Row gutter={[16, 16]}>
                                    <Col span={12}>
                                        <VTextInput
                                            field="regulatoryGuidance"
                                            label="Regulatory guidance"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('regulatoryGuidance')}
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <VTextInput
                                            field="regulator"
                                            label="Regulator"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('regulator')}
                                        />
                                    </Col>
                                    <Col span={24}>
                                        <VTipTapInput
                                            field="regulatoryGuidanceDescription"
                                            label="Exact wording from the Regulatory guide"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('regulatoryGuidanceDescription')}
                                            maxRowsToDisplay={5}
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <VLinksArrayInput
                                            label="Links"
                                            field="regulatoryGuidanceURLs"
                                            formStore={obligationFormStore}
                                            isReadOnly={readonly || isFieldReadonly('regulatoryGuidanceURLs')}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                        <Col span={24}>
                            <VBreachExamples
                                masterBreachExamples={obligation.masterBreachExamples}
                                breachExamples={obligation.breachExamples}
                                formStore={obligationFormStore}
                                readonly={readonly}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col xs={24} lg={8}>
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Controls readonly={readonly} />
                        </Col>
                        {showLinkedRegisterRecords && (
                            <Col span={24}>
                                <LinkedRegisterRecordsPanel targetType="obligation" targetId={obligation.id} />
                            </Col>
                        )}
                        <Col span={24}>
                            <TaskList
                                relatedEntity="obligation"
                                relatedEntityId={obligation.id}
                                handlingPartyIds={obligation.handlingPartyIds}
                                hideAddTask={readonly}
                                size="small"
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
        </PageController>
    );
}
